import {ChangeDetectionStrategy, ChangeDetectorRef, Component, Inject, OnDestroy, OnInit} from '@angular/core';
import {UntypedFormArray, UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, Validators} from '@angular/forms';
import {Reducers} from '@app/reducers';
import {ConfigService} from '@config/config.service';
import {ProjectContactsSortEnum} from '@crm/enum/project-contacts-sort-enum';
import {Actions, ofType} from '@ngrx/effects';
import {select, Store} from '@ngrx/store';
import {TranslateService} from '@ngx-translate/core';
import {NameResponse} from '@services/associations/model/nameResponse';
import {ProjectContactRequest} from '@services/associations/model/projectContactRequest';
import {ProjectContactResponse} from '@services/associations/model/projectContactResponse';
import {ProjectRequest} from '@services/associations/model/projectRequest';
import {ProjectResponse} from '@services/associations/model/projectResponse';
import {PermissionsModel} from '@shared/models/permission.model';
import {SharedService} from '@shared/shared.service';
import {ValidateEmptyOrObject} from '@shared/validators/empty-or-object.validator';
import {ValidateIfObject} from '@shared/validators/if-object.validator';
import {ToastrService} from 'ngx-toastr';
import {BehaviorSubject, catchError, combineLatest, EMPTY, Observable, Subject, takeUntil} from 'rxjs';
import {filter, map, take} from 'rxjs/operators';
import * as selectFromLogin from '@login/ngrx/login.selectors';
import * as mainActions from '@main/ngrx/actions/main.actions';
import * as projectsActions from '@crm/ngrx/actions/projects.actions';
import * as contractActions from '@billing/ngrx/actions/contracts.actions';
import {ContactResponse} from "@services/associations/model/contactResponse";
import {CrmService} from "@shared/services/crm.service";
import {ProjectChangeStatusRequest} from "@services/associations/model/projectChangeStatusRequest";
import { HttpErrorResponse } from "@angular/common/http";
import {CrmTaskDialogComponent} from "@crm/components/leads/crm-task-dialog/crm-task-dialog.component";
import {TaskResponse} from "@services/associations/model/taskResponse";
import {ProjectStatusHistoryResponse} from "@services/associations/model/projectStatusHistoryResponse";
import dayjs from "dayjs";
import {BillingContractModel} from "@services/back-office/model/billingContractModel";
import {BillingService} from "@shared/services/billing.service";
import {ListDialogComponent} from "@shared/components/list-dialog/list-dialog.component";
import {ListItemType} from "@shared/enum/list-item-type";
import {ProjectProgressModel} from "@services/associations/model/projectProgressModel";
import {ConfirmationDialogComponent} from "@shared/components/confirmation-dialog/confirmation-dialog.component";
import * as leadsBoardTasksActions from "@crm/ngrx/actions/leads-board.actions";
import * as leadsBoardActions from "@crm/ngrx/actions/leads-board.actions";
import {WebOwnProfileModel} from "@services/back-office/model/webOwnProfileModel";
import {MAT_DIALOG_DATA, MatDialogRef} from '@angular/material/dialog';

@Component({
    selector: 'app-project-dialog',
    templateUrl: './project-dialog.component.html',
    styleUrls: ['./project-dialog.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class ProjectDialogComponent implements OnInit, OnDestroy {
    /** Loading */
    loading$ = new BehaviorSubject(false);
    /** Form */
    projectForm: UntypedFormGroup;
    associationsForm: UntypedFormGroup;
    addContactForm: UntypedFormGroup;
    /** Check if Close Dialog after Save */
    closeDialogFlag: boolean;
    /** Item to edit */
    projectContacts: ProjectContactResponse[] = [];
    addContactVisible = false;
    /** Current User */
    currentUser$: Observable<WebOwnProfileModel | null>;
    currentUser: WebOwnProfileModel | null;
    /** Permissions */
    permissions$: Observable<PermissionsModel[] | null>;
    permissions: PermissionsModel[] | null;
    readonlyFlag: boolean;
    savedData$: Subject<ProjectResponse> = new Subject<ProjectResponse>();
    /** Subject to destroy Subscriptions */
    private destroyed$ = new Subject<void>();
    public isFailedConfirmation: boolean;
    public isStatusesHistoryOpened: boolean;
    minDate = new Date();

    comment: UntypedFormControl = new UntypedFormControl();
    failedReason: UntypedFormControl = new UntypedFormControl();
    projectStatuses$ = new BehaviorSubject<ProjectStatusHistoryResponse[] | null | undefined>(null);

    contracts: BillingContractModel[];
    tabIndex = 0;
    projectProgressIndicator: number;
    projectProgress: ProjectProgressModel[] = [
        {
            type: 'DEMO',
            started: false,
            finished: false,
        },
        {
            type: 'REGISTRATION',
            started: false,
            finished: false
        },
        {
            type: 'CONTACT',
            started: false,
            finished: false
        },
        {
            type: 'GENERAL_IMPLEMENTATION',
            started: false,
            finished: false
        },
        {
            type: 'ACCOUNTING_INITIALS',
            started: false,
            finished: false
        },
        {
            type: 'ACCOUNTING_IMPLEMENTATION',
            started: false,
            finished: false
        },
        {
            type: 'GENERAL_IMPLEMENTATION_CHECK',
            started: false,
            finished: false
        },
        {
            type: 'ACCOUNTING_IMPLEMENTATION_CHECK',
            started: false,
            finished: false
        }
    ];

    constructor(
        @Inject(MAT_DIALOG_DATA) public data: {
            edit?: boolean,
            projectToEdit?: ProjectResponse,
            contact?: ContactResponse,
        },
        public dialogRef: MatDialogRef<ProjectDialogComponent>,
        private translateService: TranslateService,
        private toastrService: ToastrService,
        private formBuilder: UntypedFormBuilder,
        private config: ConfigService,
        private store$: Store<Reducers>,
        private actions$: Actions,
        private crmService: CrmService,
        private billingService: BillingService,
        private sharedService: SharedService,
        private cdRef: ChangeDetectorRef
    ) {
        /** Select Current User */
        this.currentUser$ = this.store$.pipe(select(selectFromLogin.selectCurrentUser));
        /** Permissions */
        this.permissions$ = this.store$.pipe(select(selectFromLogin.selectPermissions));
    }

    ngOnInit(): void {
        /** Get Permissions From Observable */
        this.getPermissionsFromObservable();
        /** Get Current User From Observable */
        this.getCurrentUserFromObservable();

        if (!this.associationsForm) {
            this.initAssociationsFormArray();
        }

        combineLatest([this.permissions$, this.currentUser$]).pipe(
            filter(data => {
                return !!data[0] || !!data[1];
            }),
            take(1),
        ).subscribe(() => {
            if (!this.data.edit && this.currentUser?.id && this.permissions?.length) {
                this.patchManager();
            }
        });

        if (this.data?.edit) {
            this.updateWidth();
            this.patchForm(this.data.projectToEdit);
        }

        if (!this.projectForm) {
            this.initForm();
        }

        if (this.data.contact) {
            this.projectContacts.push({contact: {id: this.data.contact.id, name: `${this.data.contact?.lastName} ${this.data.contact?.firstName}`}, type: this?.data?.contact?.type ? this?.data?.contact?.type : "GOVERNMENT_HEAD"});
        }
        /** Watch Actions */
        this.watchSuccessProjectsActions();
        this.watchFailureProjectsActions();
        this.watchContractActions();
        this.watchTaskActions();
    }

    ngOnDestroy() {
        this.destroyed$.next();
        this.destroyed$.complete();
        this.loading$.complete();
        this.savedData$.complete();
    }

    updateWidth() {
        this.dialogRef.updateSize(this.config.rightSideDialogProps.width, this.config.rightSideDialogProps.height);
    }

    updateProjectProgress() {
        let indicatorValue = 0;
        const weight = 10 / (this.projectProgress.length * 2);
        for (let i = 0; i < this.projectProgress?.length; i++) {
            if (this.projectProgress[i].started) {
                indicatorValue += weight;
            }

            if (this.projectProgress[i].finished) {
                indicatorValue += weight;
            }
        }


        this.projectProgressIndicator = Number(indicatorValue.toFixed(2));
        this.cdRef.detectChanges();
    }

    changeProgressTask(index: number, removeTask?: boolean) {
        if (removeTask) {
            const confirmationDialog$ = this.sharedService.openDialog(
                ConfirmationDialogComponent,
                {...this.config.confirmationDialog, hasBackdrop: true},
                {
                    title: 'Після відкріплення завдання помітки про початок та завершення етупу будуть зняті. Відкріпити завдання від цього етапу?',
                    cancelText: 'common.buttons.cancel',
                    submitText: 'common.buttons.delete',
                }
            );

            confirmationDialog$.afterClosed().pipe(
                take(1)
            ).subscribe((result) => {
                if (result) {
                    delete this.projectProgress[index].task;
                    this.projectProgress[index].started = false;
                    this.projectProgress[index].finished = false;
                    this.saveProjectProgress();
                }
            });
        } else {
            const listDialog$ = this.sharedService.openDialog(
                ListDialogComponent,
                {...this.config.itemsListDialog, hasBackdrop: true},
                {
                    itemsType: ListItemType.TASK,
                    props: {keyWord: this.data.projectToEdit?.name},
                    actions: {
                        canAddItem: true,
                        canEditItem: true,
                        canDeleteItem: true
                    }
                }
            );

            listDialog$.afterClosed().pipe(
                take(1),
            ).subscribe((dialogData) => {
                if (dialogData && dialogData.data) {
                    this.proceedAddTask(index, dialogData.data);
                }
            });
        }
    }

    proceedAddTask(index: number, task: TaskResponse) {
        this.projectProgress.splice(index, 1, {...this.projectProgress[index], task: task, started: true, finished: task.status == 'DONE'});
        this.saveProjectProgress();
    }

    saveProjectProgress() {
        this.crmService.saveProjectProgress(this.data.projectToEdit!.id!, this.projectProgress).pipe(
            takeUntil(this.destroyed$),
            map((res: ProjectProgressModel[]) => {
                if (res) {
                    this.updateProjectProgress();
                }
            })
        ).subscribe();
    }

    createCrmTask(type: string, index: number) {
        if (this.readonlyFlag || !this.data.projectToEdit || !type) {
            return;
        }

        let taskType = '';

        switch (true) {
            case type == 'DEMO':
                taskType = 'DEMO';
                break;
            case type == 'REGISTRATION':
                taskType = 'REGISTRATION';
                break;
            case type == 'CONTACT':
                taskType = 'CONTRACT';
                break;
            case type == 'GENERAL_IMPLEMENTATION':
                taskType = 'OVERVIEW';
                break;
            case type == 'ACCOUNTING_INITIALS':
                taskType = 'DATA';
                break;
            case type == 'ACCOUNTING_IMPLEMENTATION':
                taskType = 'IMPLEMENTATION';
                break;
            default:
                break;
        }

        const taskDialog$ = this.sharedService.openDialog(
            CrmTaskDialogComponent,
            {...this.config.rightSideDialog, ...this.config.canMinimizeDialog},
            {project: {id: this.data.projectToEdit.id, name: this.data.projectToEdit.name}, projectContacts: this.projectContacts, type: taskType }
        );

        (<CrmTaskDialogComponent>taskDialog$.componentInstance).savedData$.pipe().subscribe((data: TaskResponse) => {
            if (data?.project?.id == this.data.projectToEdit?.id) {
                this.proceedAddTask(index, data);
            }
        });
    }

    initForm() {
        this.projectForm = this.formBuilder.group({
            name: ['', [Validators.required]],
            category: ['', [Validators.required]],
            manager: ['', [Validators.required, ValidateIfObject]],
            responsible: ['', [Validators.required, ValidateIfObject]],
            source: ['', [Validators.required]],
            state: ['NORMAL', [Validators.required]],
            delayDate: [''],
            contractType: ['', [Validators.required]],
            payerType: ['ASSOCIATION'],
            organization: ['', [ValidateEmptyOrObject]],
            counterParty: ['', [ValidateEmptyOrObject]],
            amount: ['', [Validators.pattern(this.config.regex.moneyTwoDigits)]],
        });
    }

    initAssociationsFormArray() {
        this.associationsForm = this.formBuilder.group({
            associationsFormControls: this.formBuilder.array([this.initSetAssociationsControls()])
        });
    }

    initSetAssociationsControls() {
        return this.formBuilder.group({
            association: ['', [ValidateEmptyOrObject]],
        });
    }

    getAssociationsControls() {
        return (this.associationsForm.get('associationsFormControls') as UntypedFormArray).controls;
    }

    getAssociationsControlsQuantity() {
        return (this.associationsForm.get('associationsFormControls') as UntypedFormArray).controls.length;
    }

    deleteAssociationsArrayItem(key: number) {
        if (this.getAssociationsControlsQuantity() > 1) {
            (this.associationsForm.get('associationsFormControls') as UntypedFormArray).removeAt(key);
        }
    }

    addAssociationsArrayItem() {
        (this.associationsForm.get('associationsFormControls') as UntypedFormArray).push(this.initSetAssociationsControls());
    }

    /** Get Permissions from Observable */
    getPermissionsFromObservable() {
        this.permissions$.pipe(
            filter(data => !!data),
            take(1)
        ).subscribe((permissions: PermissionsModel[] | null) => {
                this.permissions = permissions;
            }
        );
    }

    /** Get Current User From Observable */
    getCurrentUserFromObservable() {
        this.currentUser$.pipe(
            filter(data => !!data),
            take(1),
        ).subscribe((currentUser: WebOwnProfileModel | null) => {
            this.currentUser = currentUser;
        });
    }

    /** Check Permissions for DOM Elements */
    checkPermissions(permission: string): boolean {
        return this.permissions ?
            SharedService.checkPermission(permission, this.permissions[0].permissions, this.permissions[0].permissionsExists) :
            false;
    }

    checkPermissionAndOwner(permission: string, managerPermission: string, element: ProjectResponse | undefined) {
        if (this.checkPermissions(permission)) {
            return false;
        }

        return !(this.checkPermissions(managerPermission) && element?.manager?.id === this.currentUser?.id);
    }

    sortProjectContacts(a: ProjectContactResponse, b: ProjectContactResponse) {
        // @ts-ignore
        if (ProjectContactsSortEnum[a.type] < ProjectContactsSortEnum[b.type]) {
            return -1;
        }

        // @ts-ignore
        if (ProjectContactsSortEnum[a.type] > ProjectContactsSortEnum[b.type]) {
            return 1;
        }
        return 0;
    }

    patchForm(project: ProjectResponse | undefined) {
        if (!this.projectForm) {
            this.initForm();
        }

        this.projectForm.patchValue({
            ...project && project.name && {name: project.name},
            ...project && project.category && {category: project.category},
            ...project && project.manager && {manager: {id: project.manager.id, name: project.manager.name}},
            ...project && project.responsible && {responsible: {id: project.responsible.id, name: project.responsible.name}},
            ...project && project.source && {source: project.source},
            ...project && project.state && {state: project.state},
            ...project && project.contractType && {contractType: project.contractType},
            ...project && project.amount && {amount: project.amount},
            ...project && project.delayDate && {delayDate: dayjs(project.delayDate).toDate()},

            payerType: project?.counterParty?.id || project?.associations?.length ? 'COUNTERPARTY' : 'ASSOCIATION',
            ...project && project.organization && {organization: project.organization},
            ...project && project.counterParty && {counterParty: project.counterParty},
        });

        if (project && project.contacts && project.contacts.length) {
            this.projectContacts = [...project.contacts];
        }

        if (project?.counterParty?.id) {
            this.getCounterPartyContracts(project.counterParty.id);
        }

        if (project?.organization?.id) {
            this.getOrganizationContracts(project.organization.id);
        }

        if (project && project.associations && project.associations.length) {
            this.patchAssociationsForm(project.associations);
        }

        if (project && project.progress) {
            this.projectProgress = project.progress;

            if (!this.projectProgress.find(el => el.type == 'GENERAL_IMPLEMENTATION_CHECK')) {
                this.projectProgress.push({
                    type: 'GENERAL_IMPLEMENTATION_CHECK',
                    started: false,
                    finished: false
                });
            }

            if (!this.projectProgress.find(el => el.type == 'ACCOUNTING_IMPLEMENTATION_CHECK')) {
                this.projectProgress.push({
                    type: 'ACCOUNTING_IMPLEMENTATION_CHECK',
                    started: false,
                    finished: false
                });
            }

            this.updateProjectProgress();
        }

        this.projectContacts.sort(this.sortProjectContacts);
    }

    payerTypeChanged(event: any) {
        if (event?.value == 'ASSOCIATION') {
            this.projectForm.get('counterParty')?.reset();
            this.associationsForm.reset();
        } else {
            this.projectForm.get('organization')?.reset();
        }

        this.contracts = [];
    }

    getOrganizationContracts(id: string) {
        if (!id || (!this.checkPermissions('BILLING_NEW_READ') && !this.checkPermissions('BILLING_SINGLE_CONTRACT_EDIT'))) {
          return;
        }
        this.billingService.getAssociationContracts(id).pipe(
            takeUntil(this.destroyed$),
            filter(data => !!data),
            map((result: BillingContractModel[]) => {
                this.contracts = result;
                this.loading$.next(false);
            }),
            catchError((error: HttpErrorResponse) => {
                SharedService.showErrorWithTitle(
                    this.translateService,
                    this.toastrService,
                    `responseErrors.${error.message}`
                );
                this.loading$.next(false);
                return EMPTY;
            })
        ).subscribe();
    }

    getCounterPartyContracts(id: string) {
        if (!id || (!this.checkPermissions('BILLING_NEW_READ') && !this.checkPermissions('BILLING_SINGLE_CONTRACT_EDIT'))) {
            return;
        }
        this.billingService.getCounterpartyContracts(id).pipe(
            takeUntil(this.destroyed$),
            filter(data => !!data),
            map((result: BillingContractModel[]) => {
                this.contracts = result;
                this.loading$.next(false);
            }),
            catchError((error: HttpErrorResponse) => {
                SharedService.showErrorWithTitle(
                    this.translateService,
                    this.toastrService,
                    `responseErrors.${error.message}`
                );
                this.loading$.next(false);
                return EMPTY;
            })
        ).subscribe();
    }

    patchAssociationsForm(associations: NameResponse[]) {
        this.initSetAssociationsControls();
        const difference = associations.length - this.getAssociationsControlsQuantity();
        if (difference > 0) {
            for (let i = 0; i < difference; i++) {
                this.addAssociationsArrayItem();
            }
        }

        for (const i in associations) {
            const association: NameResponse = associations[i];
            const controls = this.getAssociationsControls();
            if (controls[i]) {
                controls[i].get('association')!.setValue(association);
            }
        }
    }

    patchManager() {
        if (!this.projectForm) {
            this.initForm();
        }

        this.projectForm.get('manager')?.setValue({id: this.currentUser?.id, name: this.currentUser?.name});

        if (this.data.edit && this.checkPermissionAndOwner('PROJECT_EDIT', 'PROJECT_OWN_EDIT', this.data.projectToEdit)) {
            this.readonlyFlag = true;
        }
    }

    addContactFormToggle() {
        if (!this.addContactForm) {
            this.addContactForm = this.formBuilder.group({
                contact: ['', [Validators.required, ValidateIfObject]],
                type: ['', [Validators.required]],
            });
        }

        this.addContactVisible = !this.addContactVisible;
    }

    addContact() {
        const formData = this.addContactForm.getRawValue();

        if (!formData.contact?.id || !formData.type || !this.data?.projectToEdit?.id) {
            return;
        }

        if (this.projectContacts.find(item => item.contact?.id === formData.contact.id)) {
            const contactToEditIndex = this.projectContacts.findIndex((item: ProjectContactResponse) => {
                return item.contact?.id === formData.contact.id;
            });

            if (contactToEditIndex !== -1) {
                this.projectContacts.splice(contactToEditIndex, 1, formData);
            }
        } else if (formData.contact?.id && formData.contact?.name) {
            this.projectContacts.unshift(formData);
        }

        this.projectContacts.sort(this.sortProjectContacts);
        this.addContactForm.reset();
        this.addContactVisible = false;
        this.submitForm(false);
    }

    editContact(contact: ProjectContactResponse) {
        this.addContactFormToggle();

        this.addContactForm.patchValue({
            contact: {id: contact.contact?.id, name: contact.contact?.name, description: contact.contact?.description},
            type: contact.type
        });
    }

    deleteContact(index: any) {
        this.projectContacts.splice(index, 1);
        this.submitForm(false);
    }

    /** Add Or Edit Agreement */
    submitForm(closeDialog: boolean) {
        this.closeDialogFlag = closeDialog;
        this.loading$.next(true);
        const formData = this.projectForm.getRawValue();
        const associationsForm = this.associationsForm.getRawValue();

        const contacts: ProjectContactRequest[] = this.projectContacts && this.projectContacts.length ? this.projectContacts.map((item: ProjectContactResponse) => {
            return {
                contactId: item.contact?.id,
                type: item.type,
            };
        }) : [];

        const associationIds: string[] = associationsForm.associationsFormControls && associationsForm.associationsFormControls.length
            ? associationsForm.associationsFormControls.map((item: any) => item.association?.id)
            : [];

        const request: ProjectRequest = {
            ...formData.manager && {managerId: formData.manager.id},
            ...formData.name && {name: formData.name},
            ...formData.category && {category: formData.category},
            ...formData.responsible && {responsibleId: formData.responsible.id},
            ...formData.source && {source: formData.source},
            ...formData.state && {state: formData.state},
            ...formData.contractType && {contractType: formData.contractType},
            ...formData.amount && {amount: formData.amount},
            ...formData.delayDate && {delayDate: dayjs(formData.delayDate).format('YYYY-MM-DD')},

            ...formData && formData.organization && formData.organization.id && {organizationId: formData.organization.id},
            ...formData && formData.counterParty && formData.counterParty.id && {counterPartyId: formData.counterParty.id},
            contacts,
            progress: this.projectProgress,
            ...formData && formData.counterParty && formData.counterParty.id && {associationIds}
        };

        if (!this.data.edit) {
            this.store$.dispatch(new projectsActions.AddProjects(request));
        } else if (this.data.edit && this.data.projectToEdit) {
            this.store$.dispatch(new projectsActions.EditProjects({projectId: this.data.projectToEdit.id!, request}));
        }
    }

    changeStatus(newStatus: ProjectResponse.StatusEnum) {
        if (this.readonlyFlag || !this.data.projectToEdit) {
            return;
        }

        if (this.data.projectToEdit.status == newStatus) {
            return;
        }

        if ((newStatus == 'DONE' || newStatus == 'ACTIVE') && !this.projectForm.get('organization')?.value?.id && !this.projectForm.get('counterParty')?.value?.id) {
            this.toastrService.warning('Для переходу у цей статус вкажіть платника (об\'єднання або компанію) та збережіть проект');
            return;
        }

        const statuses = SharedService.convertEnumToObject(ProjectResponse.StatusEnum);
        const currentStatusIndex = statuses.findIndex(el => el.value == this.data.projectToEdit?.status);
        const newStatusIndex = statuses.findIndex(el => el.value == newStatus);

        if (newStatusIndex < currentStatusIndex || (newStatus == 'FAIL' && this.data.projectToEdit.status == 'ACTIVE') || (newStatus == 'ACTIVE' && this.data.projectToEdit.status == 'FAIL')) {
            SharedService.showWarningWithTitle(
                this.translateService,
                this.toastrService,
                `responseErrors.project_change_status_forbidden`
            );

            return;
        }

        if (newStatus == 'ACTIVE' && this.data.projectToEdit.status != 'DONE') {
            SharedService.showWarningWithTitle(
                this.translateService,
                this.toastrService,
                `responseErrors.project_change_status_forbidden_active`
            );

            return;
        }

        if (newStatus == 'FAIL') {
            this.isFailedConfirmation = true;
        }

        if (newStatus == 'DONE') {
            const taskDialog$ = this.sharedService.openDialog(
                CrmTaskDialogComponent,
                {...this.config.rightSideDialog, ...this.config.canMinimizeDialog},
                {project: {id: this.data.projectToEdit.id, name: this.data.projectToEdit.name}, projectContacts: this.projectContacts, type: 'UX' }
            );

            (<CrmTaskDialogComponent>taskDialog$.componentInstance).savedData$.pipe().subscribe((data: TaskResponse) => {
                if (data?.project?.id == this.data.projectToEdit?.id) {
                    this.changeStatusRequest({status: newStatus});
                }
            });
        }

        if (newStatus != 'DONE' && newStatus != 'FAIL') {
            this.changeStatusRequest({status: newStatus});
        }
    }

    changeStatusRequest(statusRequest: ProjectChangeStatusRequest) {
        this.loading$.next(true);
        this.crmService.setProjectsStatus(this.data.projectToEdit!.id!, statusRequest).pipe(
            takeUntil(this.destroyed$),
            map(() => {
                this.data.projectToEdit = {...this.data.projectToEdit, status: statusRequest.status};
                this.store$.dispatch(new projectsActions.EditProjectsSuccess(this.data.projectToEdit));
                if (this.isFailedConfirmation) {
                  this.isFailedConfirmation = false;
                  this.failedReason.reset();
                  this.comment.reset();
                }
                this.loading$.next(false);
            }),
            catchError((error: HttpErrorResponse) => {
                SharedService.showErrorWithTitle(
                    this.translateService,
                    this.toastrService,
                    `responseErrors.${error.error.message}`
                );
                this.loading$.next(false);
                return EMPTY;
            })
        ).subscribe();
    }

    getStatuses($event: any) {
        this.isStatusesHistoryOpened = true;
        $event.preventDefault();

        this.crmService.getProjectsStatuses(this.data.projectToEdit!.id!).pipe(
            takeUntil(this.destroyed$),
            map((resp: ProjectStatusHistoryResponse[]) => {
                this.projectStatuses$.next(resp);
            }),
            catchError((error: HttpErrorResponse) => {
                SharedService.showErrorWithTitle(
                    this.translateService,
                    this.toastrService,
                    `responseErrors.${error.error.message}`
                );
                this.loading$.next(false);
                return EMPTY;
            })
        ).subscribe();
    }

    getDuration(index: number) {
        const history = this.projectStatuses$.getValue();
        if (!history?.length) {
          return '';
        }

        if (history[index + 1]) {
          return dayjs(history[index + 1].date).diff(history[index].date!, 'days');
        }

        return dayjs().diff(history[index].date!, 'days');
    }

    deleteStatus(status: ProjectStatusHistoryResponse) {
        this.crmService.deleteProjectsStatus(status.id!).pipe(
            takeUntil(this.destroyed$),
            map((resp: ProjectStatusHistoryResponse[]) => {
                this.projectStatuses$.next(resp);
                const newStatus = resp[resp.length - 1].status;
                this.data.projectToEdit = {...this.data.projectToEdit, status: newStatus};
                this.store$.dispatch(new projectsActions.EditProjectsSuccess(this.data.projectToEdit));
            }),
            catchError((error: HttpErrorResponse) => {
                SharedService.showErrorWithTitle(
                    this.translateService,
                    this.toastrService,
                    `responseErrors.${error.error.message}`
                );
                this.loading$.next(false);
                return EMPTY;
            })
        ).subscribe();
    }

    openCrmItem(type: string, id?: string) {
        if (!id) {
          return;
        }
        this.sharedService.openCrmItem(type, id);
    }

    editTask(task: any) {
        this.sharedService.openDialog(
            CrmTaskDialogComponent,
            {...this.config.rightSideDialog, ...this.config.canMinimizeDialog},
            {edit: true, task, projectContacts: this.data.projectToEdit?.contacts}
        );
    }

    /** Watch Actions */
    watchSuccessProjectsActions() {
        this.actions$.pipe(
            ofType(
                projectsActions.ProjectsActionTypes.AddProjectsSuccess,
                projectsActions.ProjectsActionTypes.EditProjectsSuccess
            ),
            takeUntil(this.destroyed$),
        ).subscribe((
            action:
                | projectsActions.AddProjectsSuccess
                | projectsActions.EditProjectsSuccess
        ) => {
            if (action.isSocket && action.type === '[Projects] Add Projects Success') {
                return;
            }

            if (action.type === '[Projects] Edit Projects Success' && action.payload.id !== this.data.projectToEdit?.id) {
                return;
            }

            if (action.type === '[Projects] Add Projects Success') {
                if (this.closeDialogFlag) {
                    this.closeDialog(true);
                } else {
                    if (!this.data.edit) {
                        this.data.edit = true;
                        this.updateWidth();
                    }
                    this.data.projectToEdit = action.payload;
                }
            }

            if (this.closeDialogFlag) {
                this.closeDialog(true);
            }

            this.savedData$.next(action.payload);
            this.loading$.next(false);
        });
    }

    watchFailureProjectsActions() {
        this.actions$.pipe(
            ofType(
                projectsActions.ProjectsActionTypes.AddProjectsFailure,
                projectsActions.ProjectsActionTypes.EditProjectsFailure,
            ),
            takeUntil(this.destroyed$),
        ).subscribe(() => {
            this.loading$.next(false);
        });
    }

    watchContractActions() {
        this.actions$.pipe(
            ofType(
                contractActions.ContractsActionTypes.AddContractsSuccess,
                contractActions.ContractsActionTypes.EditContractsSuccess,
                contractActions.ContractsActionTypes.UpdateContractSuccess,
                contractActions.ContractsActionTypes.DeleteContractsSuccess,
            ),
            takeUntil(this.destroyed$),
        ).subscribe(() => {
            if (this.data?.projectToEdit?.counterParty?.id) {
                this.getCounterPartyContracts(this.data.projectToEdit.counterParty.id);
            }

            if (this.data?.projectToEdit?.organization?.id) {
                this.getOrganizationContracts(this.data.projectToEdit.organization.id);
            }
        });
    }

    watchTaskActions() {
        this.actions$.pipe(ofType(leadsBoardTasksActions.LeadsBoardActionTypes.EditTasksSuccess),
            takeUntil(this.destroyed$),
        ).subscribe((action: leadsBoardActions.EditTasksSuccess) => {
            if (action.payload.id && this.projectProgress.find(el => el?.task?.id == action.payload.id)) {
                for (let i = 0; i < this.projectProgress.length; i++) {
                    if (this.projectProgress[i].task?.id && this.projectProgress[i].task!.id == action.payload.id) {
                        this.proceedAddTask(i, action.payload);
                    }
                }
            }
        });
    }

    /** Minimize Dialog */
    minimize() {
        SharedService.minimize(
            this.dialogRef.id,
            `${this.data.projectToEdit?.name}`,
            this.store$,
            this.data.projectToEdit?.id,
            false,
            '',
            `${SharedService.getTranslation(this.translateService, 'modules.crm.projects.projectDialog.title')} - ${this.data.projectToEdit?.name}`,
        );
    }

    /** Close dialog */
    closeDialog(result: boolean, data?: any) {
        this.dialogRef.close({result, data});
        this.store$.dispatch(new mainActions.CloseMinimizedDialog(this.dialogRef.id));
    }
}
